import {
  Box,
  Button,
  ClickAwayListener,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TableBody,
  TableCell,
  TableRow,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import React, { useEffect, useState } from "react";
import ArrowIcon from "../../../../shared/assets/icons/ArrowIcon";
import TableActionButton from "../../../../shared/components/tables/TableActionButton";
import Moment from "react-moment";
import ActionToast from "../../../../shared/components/ActionToast";
import { NotificationMessage, formatWarrantyFullName, getName } from "../../../../shared/models/models";
import { CustomerMyRfqsState, RfqStatusCode, RfqsQuery } from "../../models/models";
import PresetChip from "../../../../shared/components/PresetChip";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { changeRfqQuery, closeRfq, getRfqs } from "../../customerMyRfqsSlice";
import Loader from "../../../../shared/components/Loader";
import { CustomerBuyerOffersState, Offer, OfferStatusCode } from "../../../shared/models/customerMyOffersModel";
import Table from "../../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../../shared/components/SortTableHead";
import { NumericFormat } from "react-number-format";
import { productCondition } from "../../../../common/models/CommonModels";
import {
  changeQuery,
  getDocument,
  getOffers,
  seenOffer,
  selectOffer,
  setError,
  setSuccess
} from "../../../offersReceived/customerReceivedOffersSilce";
import ImageBox from "../../../../shared/components/ImageBox";
import colors from "../../../../shared/themes/colors";
import { useNavigate } from "react-router-dom";
import CartIcon from "../../../../shared/assets/icons/CartIcon";
import TablePagination from "../../../../shared/components/tables/TablePagination";
import MyOffersDetails from "./MyOffersDetails";
import TableEmpty from "../../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../../shared/assets/icons/BoxSearchIcon";
import { fulfillmentPresets, matchStatusCodes, visibilityPresets } from "../../shared/models/models";
import DownloadFile from "../../../../shared/components/DownloadFile";

interface MyOffersProps {
  closeOffers: () => void;
  openOffers: (rfqId: string) => void;
  rfqId: string;
}

const MyOffers = ({ closeOffers, rfqId, openOffers }: MyOffersProps) => {
  const { query, offers, offer, loading, success, error } = useAppSelector<CustomerBuyerOffersState>(
    (state: any) => state.customerReceivedOffers
  );

  const { query: rfqsQuery, rfqs } = useAppSelector<CustomerMyRfqsState>((state: any) => state.customerMyRfqs);

  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const navigate = useNavigate();

  const columns: TableColumnDefinition[] = [
    { title: "Image" },
    { title: "Company details" },
    { title: "Product" },
    { title: "Shipping" },
    { title: "My RFQ details" },
    { title: "Offer", columnAlign: "center" },
    { title: undefined }
  ];

  const rfqStatusCode = rfqs?.items.filter((x) => x.id === rfqId).find((x) => x)?.statusCode;
  const offersCount = rfqs?.items.filter((x) => x.id === rfqId).find((x) => x)?.offersCount;

  const handleCloseRfq = async () => {
    if (rfqId) {
      await dispatch(closeRfq(rfqId));
      await dispatch(getRfqs(rfqsQuery));
    }
  };

  const handleGoToCreate = () => {
    navigate("/trading/my-rfqs/new");
  };

  const handleSort = (parameter: number) => {
    const sortDesc = query.sortBy !== parameter ? false : !query.sortDesc;
    const newQuery = { ...query, sortBy: parameter, sortDesc: sortDesc };

    dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageSizeChange = async (size: number) => {
    const newQuery = { ...query, pageNo: 1, pageSize: size, quotationRequestId: rfqId };
    await dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handlePageNoChange = async (no: number) => {
    const newQuery = { ...query, pageNo: no, quotationRequestId: rfqId };
    await dispatch(changeQuery(newQuery));
    dispatch(getOffers(newQuery));
  };

  const handleVisibilityPresetChange = (event: SelectChangeEvent) => {
    const visibilityPresetValue = Number(event.target.value);
    const selectedVisibilityPreset = visibilityPresets.find(({ value }) => visibilityPresetValue === value);

    if (selectedVisibilityPreset) {
      const newQuery = { ...rfqsQuery, ...selectedVisibilityPreset.queryParams };
      dispatch(changeRfqQuery(newQuery));
    }
  };

  const handleFulfillmentPresetChange = (queryParams: Partial<RfqsQuery>) => {
    const newQuery = { ...rfqsQuery, ...queryParams };
    dispatch(changeRfqQuery(newQuery));
  };

  const handleGoToMyOffers = (rfqId: string) => {
    openOffers(rfqId);
  };

  const [offersDetails, setOffersDetails] = useState(false);

  const handleClickAway = () => {
    dispatch(selectOffer(null));
  };

  const handleOfferSelect = (item: Offer) => {
    dispatch(selectOffer(item));
    if (item.statusCode === OfferStatusCode.new) {
      dispatch(seenOffer(item.id));
    }
    setOffersDetails(true);
  };

  const init = async () => {
    if (rfqId) {
      const newQuery = { ...query, quotationRequestId: rfqId };
      await dispatch(getOffers(newQuery));
    }
  };

  useEffect(() => {
    init();
  }, [rfqId]);

  return (
    <>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      <Box className="flex flex-col flex-1 w-full">
        <Toolbar className="flex items-center">
          <Box className="flex items-center justify-between w-full">
            <Typography fontSize={24} fontWeight={500} className="heading-2">
              My Offers
            </Typography>

            <Button variant="contained" onClick={handleGoToCreate}>
              Create new RFQ
            </Button>
          </Box>
        </Toolbar>

        <Box className="flex v-fill w-full">
          <Box sx={{ minWidth: "348px", borderRight: "1px solid #C7C9CD" }}>
            <Box className="px-4 py-3">
              <Box className="flex items-center">
                <Typography fontSize={24} fontWeight={500} className="heading-2">
                  My RFQs
                </Typography>

                <Select
                  className="ml-2"
                  displayEmpty
                  defaultValue={rfqsQuery.visibilityCode.toString()}
                  value={rfqsQuery.visibilityCode.toString()}
                  onChange={handleVisibilityPresetChange}
                >
                  {visibilityPresets.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value} disabled={preset.disabled}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box className="flex flex-wrap gap-4 mt-4">
                {fulfillmentPresets.map((preset) => (
                  <PresetChip
                    key={preset.value}
                    label={preset.label}
                    active={matchStatusCodes(rfqsQuery.statusCodes, preset.queryParams.statusCodes)}
                    onClick={() => handleFulfillmentPresetChange(preset.queryParams)}
                  />
                ))}
              </Box>
            </Box>

            <Box
              className="hide-scrollbar"
              sx={{ height: "calc(100% - 166px)", overflowY: "scroll", overflowX: "hidden" }}
            >
              {rfqs?.items.map((item) => (
                <>
                  {item.offersCount > 0 && (
                    <Box
                      className={item.id === rfqId ? "my-rfqs-items active" : "my-rfqs-items"}
                      onClick={() => handleGoToMyOffers(item.id)}
                    >
                      <Box className="flex items-center gap-4">
                        <Box>
                          <Box className="text-14-bold" color="#333333">
                            <Tooltip title={item.productNumber.length > 22 ? item.productNumber : null} placement="top">
                              <Box className="ellipsis-hidden" sx={{ width: "170px" }}>
                                {item.productNumber}
                              </Box>
                            </Tooltip>
                          </Box>

                          <Box className="text-12-regular" color="#333333">
                            <Tooltip
                              placement="top"
                              title={item?.productAlternativeNumber?.length > 22 ? item.productAlternativeNumber : null}
                            >
                              <Box className="ellipsis-hidden" sx={{ width: "170px" }}>
                                {item.productAlternativeNumber}
                              </Box>
                            </Tooltip>
                          </Box>

                          <Box>
                            <Tooltip title={item.manufacturer.name.length > 22 ? item.manufacturer.name : null}>
                              <Box className="text-12-medium ellipsis-hidden" color="#333333" sx={{ width: "170px" }}>
                                {item.manufacturer.name}
                              </Box>
                            </Tooltip>
                            <Tooltip title={item.category.name.length > 22 ? item.category.name : null}>
                              <Box className="text-12-medium ellipsis-hidden" color="#333333" sx={{ width: "170px" }}>
                                {item.category.name}
                              </Box>
                            </Tooltip>
                          </Box>
                          <Box className="text-12-medium">
                            Last call on{"\u00A0"}
                            <Moment date={item.lastCall} format="DD.MM.YYYY" />
                          </Box>
                        </Box>

                        <Box
                          className="px-2 py-1 text-12-medium"
                          color="#333333"
                          style={{ background: "#E2E6FB", borderRadius: "4px", height: "fit-content" }}
                        >
                          {item.offersCount > 10 ? "10+" : item.offersCount} offers
                        </Box>
                      </Box>

                      {item.statusCode === RfqStatusCode.open && (
                        <Box>
                          <TableActionButton stopPropagation={true}>
                            <MenuItem
                              onClick={() =>
                                confirm({
                                  title: "Close RFQ",
                                  text: `Are you sure you want to close ${getName(
                                    item.manufacturer.name,
                                    item.productNumber
                                  )} RFQ?`,
                                  confirmButton: "Yes, close",
                                  closeButton: "No",
                                  severity: "error",
                                  onConfirm: handleCloseRfq
                                })
                              }
                            >
                              <DeleteOutlinedIcon />
                              Close RfQ
                            </MenuItem>
                          </TableActionButton>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              ))}

              {!rfqs?.items.length && !offersCount && (
                <Box className="px-4 py-3 text-16-bold text-center" color={"#333333"}>
                  You don’t have such RFQs
                </Box>
              )}
            </Box>
          </Box>

          <Box className="flex flex-col w-full pt-2">
            <Box className="flex justify-between px-2">
              <Button onClick={closeOffers}>
                <ArrowIcon className="rotate-180" />
                <Box className="ml-4">Back to My RFQs</Box>
              </Button>
              {rfqStatusCode === RfqStatusCode.open && <Button onClick={handleCloseRfq}>Archive this RFQ</Button>}
            </Box>

            <Box className="v-fill w-full">
              {loading ? (
                <Loader />
              ) : !rfqs?.items.length && !offersCount ? (
                <TableEmpty iconComponent={BoxSearchIcon} text={"You have no offers yet"}></TableEmpty>
              ) : (
                <Table className="px-4">
                  <SortTableHead
                    columns={columns}
                    sortBy={query.sortBy}
                    sortDesc={query.sortDesc}
                    onSort={handleSort}
                  />

                  <TableBody>
                    {offers?.items.map((item) => {
                      return (
                        <TableRow
                          key={item.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOfferSelect(item);
                          }}
                          selected={item.id === offer?.id}
                        >
                          <TableCell className="break-all" sx={{ maxWidth: "250px" }}>
                            <ImageBox size="small" src={item.imageFile?.content} />
                          </TableCell>

                          <TableCell>
                            <Box className="text-14-bold" color={"#333333"}>
                              {item.supplier.name}
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box className="text-14-medium" color={"#333333"}>
                              {productCondition(item.productConditionCode)}
                            </Box>

                            <Box className="text-18-medium" color={"#333333"}>
                              <NumericFormat
                                value={item.unitPrice?.value}
                                displayType="text"
                                thousandSeparator=" "
                                suffix="€"
                              />
                              /pc
                            </Box>

                            <Box className="text-12-regular" color={"#333333"}>
                              {formatWarrantyFullName(item.warrantyPeriodMonths)}
                            </Box>
                          </TableCell>

                          <TableCell></TableCell>

                          <TableCell>
                            <Box>
                              <Box className="text-12-regular" color={colors.secondary.gray40}>
                                Created on{"\u00A0"}
                                <Moment date={item.createdDate} format="DD.MM.YYYY" />
                              </Box>

                              <Box className="text-12-regular" color={colors.secondary.gray40}>
                                {"#" + item.quotationRequestNumber}
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <DownloadFile item={item} title="Download offer in .pdf" request={getDocument} />
                          </TableCell>

                          <TableCell>
                            <IconButton>
                              <CartIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>

            <TablePagination
              variant="bottom"
              pageNo={query.pageNo}
              pageSize={query.pageSize}
              totalCount={offers?.totalCount}
              onPageNoChange={handlePageNoChange}
              onPageSizeChange={handlePageSizeChange}
            />

            {offer && (
              <ClickAwayListener onClickAway={handleClickAway}>
                <div className="absolute">
                  <Drawer
                    variant="persistent"
                    anchor="right"
                    open={offersDetails}
                    onClose={() => setOffersDetails(false)}
                    hideBackdrop
                    elevation={0}
                    PaperProps={{
                      sx: {
                        background: "#FFF !important",
                        borderLeft: "1px solid #D5D6D9",
                        marginTop: "150px",
                        height: "calc(100% - 150px)"
                      }
                    }}
                  >
                    <MyOffersDetails rfqStatusCode={rfqStatusCode} rfqs={rfqs} />
                  </Drawer>
                </div>
              </ClickAwayListener>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};
export default MyOffers;
